import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { formatPhoneNumber, removeNonDigits } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Table, Chip, SearchField, FormRow, SwitchField, useForm } from '@frontend/design-system';

export const PhoneNumbersTable = ({
  tenantLocation,
  phoneNumbers,
  selectedPhoneNumberIds,
  trackingIdBase,
  setSelectedPhoneNumberIds,
}: {
  tenantLocation: SettingsTenantLocation;
  phoneNumbers: PhoneNumber[];
  selectedPhoneNumberIds: string[];
  trackingIdBase?: string;
  setSelectedPhoneNumberIds: (phoneNumbers: string[]) => void;
}) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();

  const [filteredLocations, setFilteredLocations] = useState<string[]>(() => {
    // only show locations that have phone numbers
    const uniqueLocations = new Set(phoneNumbers.map((phoneNumber) => phoneNumber.locationId).filter(Boolean));
    return Array.from(uniqueLocations);
  });

  const form = useForm({
    fields: {
      search: {
        type: 'text',
        debounce: 300,
      },
      showSelected: {
        type: 'switch',
      },
    },
  });

  const isUnify = tenantLocation.locationType === 'unify';

  const filteredTableData = useMemo(() => {
    const searchValue = form.values.search?.trim();
    const hasSearchValue = !!searchValue;
    const searchDigits = hasSearchValue ? removeNonDigits(searchValue) : '';

    return phoneNumbers.filter((row) => {
      // Filter by location (if applicable)
      if (isUnify && filteredLocations.length && !filteredLocations.includes(row.locationId ?? '')) {
        return false;
      }

      // Filter by selection
      if (form.values.showSelected && !selectedPhoneNumberIds.includes(row.id)) {
        return false;
      }

      // Filter by search
      if (hasSearchValue && !String(row.phoneNumber)?.includes(searchDigits)) {
        return false;
      }

      return true;
    });
  }, [phoneNumbers, form.values.search, filteredLocations, form.values.showSelected, selectedPhoneNumberIds]);

  return (
    <>
      <div>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            & > div {
              display: inline-block;
            }
          `}
        >
          {isUnify && (
            <>
              <LocationFilterMenu
                showClearAll={false}
                locations={tenantLocation.children ?? []}
                selectedOptions={filteredLocations}
                onChange={setFilteredLocations}
                trackingIdBase={trackingIdBase ? trackingIdBase + '-location-filter' : undefined}
              />
              <SearchField
                label={t('Search Phone Numbers')}
                {...form.getFieldProps('search')}
                css={css`
                  width: 260px;
                `}
                data-trackingid={trackingIdBase ? trackingIdBase + '-search' : undefined}
              />
            </>
          )}
        </div>

        <FormRow>
          <SwitchField
            {...form.getFieldProps('showSelected')}
            name='showSelected'
            label={t('Show selected numbers')}
            disabled={!form.values.showSelected && selectedPhoneNumberIds.length === 0}
            trackingId={trackingIdBase ? trackingIdBase + '-show-selected-toggle' : undefined}
            css={css`
              display: flex;
              flex-direction: row-reverse;
              gap: ${theme.spacing(1)};
              margin-top: ${theme.spacing(2)};
              margin-right: ${theme.spacing(0.25)};
            `}
          />
        </FormRow>
      </div>

      <Table
        data={filteredTableData}
        tableStyle={css`
          max-height: 600px;
        `}
        isSelectable={true}
        // The table's internal selection state is preserved across data changes, using stable phone number IDs
        uniqueRowId={(phoneNumber) => phoneNumber.id}
        globalTrackingId={trackingIdBase ? trackingIdBase + '-table' : undefined}
        emptyStateConfig={{
          description: t('No phone numbers'),
          header: '',
          type: 'phone_data',
        }}
        colConfig={[
          {
            Header: t('Phone Number'),
            accessor: (phoneNumber) => phoneNumber.phoneNumber,
            id: 'phone-number',
            cellRenderer: (phoneNumber) => formatPhoneNumber(phoneNumber),
            width: 180,
          },
          {
            id: 'phoneNumberName',
            Header: t('Name'),
            accessor: (item: PhoneNumber) => (item.displayName ? item.displayName : '--'),
            width: 180,
          },
          {
            Header: t('Location'),
            accessor: (phoneNumber) => phoneNumber.locationId,
            cellRenderer: (locationId) =>
              locationId ? <Chip.SingleChip>{getScopeName(locationId)}</Chip.SingleChip> : '',
            id: 'location-name',
            sortType: 'caseInsensitive',
            width: 190,
            omit: !isUnify,
          },
        ]}
        rowSelectionConfig={{
          hideBulkSelection: true,
          // When the user manually toggles a row, compute which selected IDs are currently filtered out and merge
          // them back with the newly selected visible items, so toggling a visible row never discards hidden
          // selections.
          onSelectionToggle: (_, selectedData) => {
            const visiblePhoneNumberIds = new Set(filteredTableData.map((pn) => pn.id));
            const filteredOutSelectedIds = selectedPhoneNumberIds.filter((id) => !visiblePhoneNumberIds.has(id));
            setSelectedPhoneNumberIds([...filteredOutSelectedIds, ...selectedData.map((pn) => pn.id)]);
          },
          // Use actual phone number IDs as keys to ensures the initial checked state is correctly mapped to the
          // right rows.
          initialState: Object.fromEntries(selectedPhoneNumberIds.map((id) => [id, true])),
        }}
      />
    </>
  );
};
